import React, { useState, useLayoutEffect, useContext } from 'react'
import { graphql } from 'gatsby';
import { css } from '@emotion/react';
import { ThemeContext } from '../components/theme-provider';
import SEO from '../components/seo';
import FadeIn from '../components/fade-in';
import PortableText from '../components/portable-text';
import Image from 'gatsby-plugin-sanity-image';
import Button from '../components/button';
import ArrowUpRight from '../images/svg/arrow-up-right.svg';
import CollaboratorsTable from '../components/collaborators-table';
import ImageButton from '../components/image-button';

export default function People({data}) {
  const theme = useContext(ThemeContext);

  const [activePerson, setActivePerson] = useState(null);

  useLayoutEffect(() => {
    theme.setLogoColorDark(true);
    theme.setMenuColorDark(true);
    theme.setBackgroundColor(null);
  }, []);

  return (
    <div>
      <SEO
        title="People"
        description={data.people?.introText}
      />
      <div
        css={css`
          display: grid;
          grid-template-columns: repeat(12, 1fr);
          gap: var(--gutter);
          padding: 0 var(--margin);
          margin-top: 21rem;

          @media (max-width: 900px) {
            margin-top: 14rem;
          }
        `}
      >
        <FadeIn
          className="type--large"
          css={css`
            grid-column-start: 7;
            grid-column-end: span 6;
            margin-bottom: 16rem;

            @media (max-width: 900px) {
              grid-column-start: 1;
              grid-column-end: span 12;
              margin-bottom: 6rem;
            }
          `}
        >
          {data.people?.introText}
        </FadeIn>
        <FadeIn
          css={css`
            grid-column: span 12;
            margin-bottom: 5rem;
          `}
        >
          {data.people?.principalPeople.length > 0 && data.people.principalPeople
            .map((person, i) => (
              <div
                key={i}
                css={css`
                  display: grid;
                  grid-template-columns: repeat(12, 1fr);
                  gap: var(--gutter);
                  padding-top: 3rem;
                  border-top: 1px solid var(--black);
                  margin-bottom: 4rem;
                  opacity: ${(activePerson !== null && activePerson !== i) ? 0.3 : 1};
                  transition: opacity 0.6s var(--curve);

                  &:first-of-type {
                    @media (max-width: 900px) {
                      border-top: 0;
                    }
                  }
                `}
              >
                <div
                  css={css`
                    grid-column: span 4;
                    line-height: 0;

                    @media (max-width: 1024px) {
                      grid-column: span 5;
                    }

                    @media (max-width: 900px) {
                      grid-column: span 12;
                    }
                  `}
                  onMouseEnter={() => setActivePerson(i)}
                  onMouseLeave={() => setActivePerson(null)}
                >
                  {(!person.linkedin && person.image) && <Image
                    {...person.image}
                    width={660}
                    style={{
                      width: '100%',
                      border: '1px solid var(--black)',
                    }}
                  />}
                  {(person.linkedin && person.image) && <ImageButton
                    image={person.image}
                    external={true}
                    url={person.linkedin}
                    text="LinkedIn"
                    arrow={<ArrowUpRight />}
                  />}
                </div>
                <div
                  css={css`
                    grid-column-start: 7;
                    grid-column-end: span 6;

                    @media (max-width: 900px) {
                      grid-column-start: 1;
                      grid-column-end: span 12;
                      margin-top: 2rem;
                    }
                  `}
                >
                  <div
                    className="type--medium"
                    css={css`
                      font-family: 'Maax Medium', sans-serif;
                    `}
                  >
                    {person.name}
                  </div>
                  <div
                    css={css`
                      margin-bottom: 2.5rem;
                    `}
                  >
                    {person.position}
                  </div>
                  <div
                    css={css`
                      column-count: 2;
                      column-gap: 2rem;
                      
                      @media (max-width: 1024px) {
                        column-count: 1;
                      }
                    `}
                  >
                    <PortableText blocks={person._rawText} />
                  </div>
                </div>
              </div>
            ))
          }
        </FadeIn>
      </div>
      <FadeIn
        css={css`
          display: grid;
          grid-template-columns: repeat(12, 1fr);
          gap: var(--gutter);
          background-color: var(--gray-3);
          padding: 8rem var(--margin) 5rem;
          border: 1px solid var(--black);
          border-right: none;
          border-left: none;

          @media (max-width: 900px) {
            padding-top: 5rem;
          }
        `}
      >
        <div
          css={css`
            grid-column: span 3;

            @media (max-width: 900px) {
              grid-column: span 12;
            }
          `}
        >
          <div
            className="type--large"
            css={css`
              margin-bottom: 3.5rem;

              @media (max-width: 900px) {
                margin-bottom: 4rem;
              }
            `}
          >
            Team
          </div>
          <div
            css={css`
              margin-bottom: 2.5rem;
            `}
          >
            {data.people?.opportunitiesText}
          </div>
          {data.people?.opportunitiesLink && <Button
            external={true}
            url={data.people.opportunitiesLink}
            text="Current Opportunities"
            arrow={<ArrowUpRight />}
          />}
        </div>
        <div
          css={css`
            display: grid;
            grid-template-columns: repeat(6, 1fr);
            gap: var(--gutter);
            grid-column-start: 7;
            grid-column-end: span 6;

            @media (max-width: 900px) {
              grid-column-start: 1;
              grid-column-end: span 12;
              margin-top: 4rem;
            }
          `}
        >
          {data.people?.teamMembers.length > 0 && data.people.teamMembers
            .map((person, i) => (
              <div
                key={i}
                css={css`
                  grid-column: span 3;
                  padding: 2rem 0;
                  border-top: 1px solid var(--black);

                  @media (max-width: 600px) {
                    grid-column: span 6;
                  }
                `}
              >
                <div
                  className="type--medium"
                  css={css`
                    font-family: 'Maax Medium', sans-serif;
                  `}
                >
                  {person.name}
                </div>
                <div
                  css={css`
                    margin-bottom: 3.5rem;
                  `}
                >
                  {person.position}
                </div>
                <div
                  css={css`
                    margin-bottom: 5rem;
                  `}
                >
                  <PortableText blocks={person._rawText} />
                </div>
              </div>
            ))
          }
        </div>
      </FadeIn>
      <div
        css={css`
          padding: 5rem var(--margin);
          margin-bottom: var(--footer-margin);
        `}
      >
        <div
          className="type--large"
          css={css`
            margin-bottom: 7rem;
          `}
        >
          Collaborators
        </div>
        <CollaboratorsTable collaborators={data.people?.collaborators} />
      </div>
    </div>
  );
}

export const query = graphql`
  query {
    people: sanityPeople(_id: {regex: "/(drafts\\\\.)?people/"}) {
      introText
      principalPeople {
        _key
        image {
          ...Image
        }
        linkedin
        name
        position
        _rawText
      }
      opportunitiesText
      opportunitiesLink
      teamMembers {
        name
        position
        _rawText
      }
      collaborators {
        _key
        name
        specialty
        location
        url
        hoverType
        hoverImage {
          asset {
            gatsbyImageData(width: 1980, formats: WEBP)
          }
        }
        hoverFullBleedImage {
          asset {
            gatsbyImageData(width: 1980, formats: WEBP)
          }
        }
        hoverColor {
          value
        }
      }
    }
  }
`
